import { ShippingZoneMapperParameter, ShippingZoneFactory } from "../models/shipping-zones.js";
import shippingProviderMixin from "@/v2/mixins/shipping-providers.js";
import httpMixin from "@/v2/mixins/http.js";
import mapperMixin from "@/v2/mixins/mapper.js";

export default {
    mixins: [httpMixin, mapperMixin, shippingProviderMixin],
    data() {
        return {
            shippingZoneUrl: window.bo.shippingZoneUrl,
        };
    },
    methods: {
        _shippingZoneMapper(shippingZone) {
            return {
                id: shippingZone.id,
                name: shippingZone.name,
                country: shippingZone.country,
                description: shippingZone.description,
                zone_regions: shippingZone.zone_regions,
                shipping_methods: shippingZone.shipping_methods,
                show_action_menu: shippingZone.show_action_menu,
                bo_can_delete: shippingZone.bo_can_delete,
            };
        },

        _shippingZoneRegionMapper(shippingZoneRegion) {
            return {
                id: shippingZoneRegion.id,
                shipping_zone_id: shippingZoneRegion.shipping_zone_id,
                type: shippingZoneRegion.type,
                state: shippingZoneRegion.state,
                area: shippingZoneRegion.area,
                postcode_from: shippingZoneRegion.postcode_from,
                postcode_to: shippingZoneRegion.postcode_to,
            };
        },

        _shippingMethodMapper(shippingMethod) {
            return {
                id: shippingMethod.id,
                shipping_zone_id: shippingMethod.shipping_zone_id,
                sequence: shippingMethod.sequence,
                name: shippingMethod.name,
                shipping_cost: shippingMethod.shipping_cost,
                min_shipping_cost: shippingMethod.min_shipping_cost,
                shipping_class_no_cost: shippingMethod.shipping_class_no_cost,
                min_order_amount: shippingMethod.min_order_amount,
                max_order_amount: shippingMethod.max_order_amount,
                capacity_limit: shippingMethod.capacity_limit,
                time_increment: shippingMethod.time_increment,
                type: shippingMethod.type,
                status: shippingMethod.status,
                remarks: shippingMethod.remarks,
                availability: shippingMethod.availability,
                availability_interval: shippingMethod.availability_interval,
                shipping_availability_id: shippingMethod.shipping_availability_id,
                shipping_providers: shippingMethod.shipping_providers,
                shipping_class_rate: shippingMethod.shipping_class_rates,
                shipping_table_rate: shippingMethod.shipping_table_rate,
                bo_can_delete: shippingMethod.bo_can_delete,
                show_action_menu: shippingMethod.show_action_menu,
                checked: shippingMethod.checked,
                shipping_classes: shippingMethod.shipping_classes,
                shipping_class_labels: shippingMethod.shipping_class_labels,
                min_table_rate: shippingMethod.min_table_rate,
                max_table_rate: shippingMethod.max_table_rate,
                table_rate_unit: shippingMethod.table_rate_unit,
                calculation_mode: shippingMethod.calculation_mode,
                measured_by: shippingMethod.measured_by,
            };
        },

        _shippingMethodProviderMapper(shippingMethodProvider) {
            return {
                id: shippingMethodProvider.id,
                shipping_method_id: shippingMethodProvider.shipping_method_id,
                shipping_provider_id: shippingMethodProvider.shipping_provider_id,
                shipping_vehicle_type_id: shippingMethodProvider.shipping_vehicle_type_id,
                min_order_percentage: shippingMethodProvider.min_order_percentage,
                shipping_provider: shippingMethodProvider.shipping_provider,
                shipping_vehicle_type: shippingMethodProvider.shipping_vehicle_type,
            };
        },

        _shippingClassRateMapper(shippingClassRate) {
            return {
                shipping_class_id: shippingClassRate.shipping_class_id,
                shipping_method_id: shippingClassRate.shipping_method_id,
                cost: shippingClassRate.cost,
                shipping_class: shippingClassRate.shipping_class,
            };
        },

        _shippingTableRateMapper(shippingTableRate) {
            return {
                id: shippingTableRate.id,
                shipping_method_id: shippingTableRate.shipping_method_id,
                amount_from: shippingTableRate.amount_from,
                amount_to: shippingTableRate.amount_to,
                price: shippingTableRate.price,
                unit: shippingTableRate.unit,
            };
        },

        parseShippingZone(shippingZone) {
            return ShippingZoneFactory.construct(
                this.createMapperParameter(new ShippingZoneMapperParameter()),
                shippingZone
            );
        },

        parseShippingZones(shippingZones) {
            let parsedShippingZones = [];

            shippingZones.forEach((shippingZoneData) => {
                const shippingZone = this.parseShippingZone(shippingZoneData);
                parsedShippingZones.push(shippingZone);
            });

            return parsedShippingZones;
        },

        getShippingZone(id, payload, success, failure) {
            return this.getData(
                `${this.shippingZoneUrl}/${id}`,
                payload,
                (response) => {
                    success(this.parseShippingZone(response.data));
                },
                failure
            );
        },

        getShippingZones(payload, success, failure) {
            return this.getData(
                this.shippingZoneUrl,
                payload,
                (response) => {
                    success(this.parseShippingZones(response.data.data), response.data.meta);
                },
                failure
            );
        },

        createShippingZone(payload, success, failure) {
            return this.postData(
                this.shippingZoneUrl,
                payload,
                (response) => {
                    success(this.parseShippingZone(response.data));
                },
                failure
            );
        },

        updateShippingZone(id, payload, success, failure) {
            return this.putData(
                `${this.shippingZoneUrl}/${id}`,
                payload,
                (response) => {
                    success(this.parseShippingZone(response.data));
                },
                failure
            );
        },

        createOrUpdateShippingZone(id = null, payload, success, failure) {
            if (id == null) {
                return this.createShippingZone(payload, success, failure);
            } else {
                return this.updateShippingZone(id, payload, success, failure);
            }
        },

        deleteShippingZone(id, payload, success, failure) {
            return this.deleteData(
                `${this.shippingZoneUrl}/${id}`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        deleteShippingZones(payload, success, failure) {
            return this.postBulkData(
                this.shippingZoneUrl,
                null,
                null,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        refreshShippingZone(id, shippingZones, success, failure) {
            this.getShippingZone(
                id,
                this.shippingZoneUrl,
                null,
                (shippingZone) => {
                    shippingZones[id] = shippingZone;
                    if (success) success(shippingZone);
                },
                failure
            );
        },

        getCountries(url, payload, success, failure) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        getStates(url, payload, success, failure) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        getShippingMethodsRemainingUses(url, date, shippingMethodIds, success, failure, showLoadingScreen = true) {
            return this.getData(
                `${url}/remaining_uses`,
                {
                    params: {
                        date: date,
                        shipping_method_id: shippingMethodIds.join(","),
                    },
                },
                (response) => {
                    success(response.data);
                },
                failure,
                showLoadingScreen
            );
        },
    },
};
